import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as SparkIcon,
  EmojiEvents as TrophyIcon,
  Groups as GroupsIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { TABLES, getCurrentSchoolYear, getSchoolYears } from '../lib/tables';
import { useAuth } from '../context/AuthContext';
import { logAuditEvent } from '../lib/auditLog';
import SchoolYearSelect from '../components/SchoolYearSelect';
import {
  buildDataset,
  buildFeatureVectors,
  buildProfiles,
  buildRetrospective,
  forecastEndOfYear,
  kMeans,
  recommend,
  schoolMonthIndex,
} from '../lib/intelligence';
import { CATEGORY_LABELS, getPreviousSchoolYear } from '../lib/statistics';

const createSessionId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function Suggestions() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [raw, setRaw] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);
  const [mode, setMode] = useState(0);
  const [retroYear, setRetroYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const loggedSession = useRef(null);
  const loadingRef = useRef(true);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    let mounted = true;
    const timeout = setTimeout(() => {
      if (mounted && loadingRef.current) {
        setError('Eroare la incarcare. Reincarca pagina.');
        setLoading(false);
      }
    }, 10000);
    const load = async () => {
      try {
        const [studentsRes, requestsRes, categoriesRes, activitiesRes, modulesRes, typesRes, calendarRes, years] =
          await Promise.all([
            supabase.from(TABLES.profiles).select('id, nume, prenume, clasa, specializare').eq('rol', 'elev'),
            supabase
              .from(TABLES.credit_requests)
              .select('id, id_elev, id_tip_credit, id_module, id_activitate_predefinita, nume_activitate, credite_solicitate, credite_acordate, status, data_cerere, data_validare, an_scolar')
              .eq('status', 'validat'),
            supabase.from(TABLES.credits_by_category).select('id_elev, an_scolar, credite_curriculare, credite_extracurriculare, credite_transferabile, credite_totale'),
            supabase
              .from(TABLES.predefined_activities)
              .select('id, id_tip_credit, id_module, nume_activitate, descriere, ore_recomandate, credite_per_ora, credite_acordate, este_activ')
              .eq('este_activ', true),
            supabase.from(TABLES.modules).select('id, cod_module, nume_module, credite_tinta, ordine').order('ordine'),
            supabase.from(TABLES.credit_types).select('id, nume_tip, categorie'),
            supabase.from('academic_calendar_v2').select('an_scolar, id_module, data_start, data_end'),
            getSchoolYears(TABLES.credit_requests),
          ]);
        if (!mounted) return;
        setRaw({
          students: studentsRes.data || [],
          requests: requestsRes.data || [],
          categories: categoriesRes.data || [],
          activities: activitiesRes.data || [],
          modules: modulesRes.data || [],
          creditTypes: typesRes.data || [],
          calendar: calendarRes.data || [],
        });
        setYearOptions(years);
        setRetroYear((previous) => previous ?? getPreviousSchoolYear(getCurrentSchoolYear()));
        setLoading(false);
      } catch (err) {
        console.error('Suggestions load error:', err);
        if (!mounted) return;
        setError('Eroare la incarcare. Reincarca pagina.');
        setLoading(false);
      } finally {
        clearTimeout(timeout);
      }
    };
    load();
    return () => { mounted = false; clearTimeout(timeout); };
  }, []);

  const currentYear = getCurrentSchoolYear();
  const dataset = useMemo(() => (raw ? buildDataset(raw) : null), [raw]);
  const profiles = useMemo(() => (dataset ? buildProfiles(dataset, currentYear) : new Map()), [dataset, currentYear]);
  const retroProfiles = useMemo(
    () => (dataset && retroYear ? buildProfiles(dataset, retroYear) : new Map()),
    [dataset, retroYear],
  );
  const cluster = useMemo(() => kMeans(buildFeatureVectors(profiles), 3), [profiles]);

  const suggestions = useMemo(
    () => (dataset && profile ? recommend(dataset, profiles, profile.id, { k: 5 }) : []),
    [dataset, profiles, profile],
  );
  const retrospective = useMemo(
    () => (dataset && profile && retroProfiles.size > 0 ? buildRetrospective(dataset, retroProfiles, profile.id) : null),
    [dataset, retroProfiles, profile],
  );

  const myProfile = profile ? profiles.get(profile.id) : null;
  const classPeers = useMemo(
    () => (myProfile ? [...profiles.values()].filter((entry) => entry.clasa === myProfile.clasa) : []),
    [myProfile, profiles],
  );
  const myCluster = myProfile ? cluster.clusters.get(profile.id) : null;
  const clusterMembers = useMemo(
    () => (myCluster !== undefined ? cluster.members[myCluster] || [] : []),
    [cluster, myCluster],
  );
  const clusterActivities = useMemo(() => {
    if (!dataset || !myProfile) return [];
    const mine = new Set(myProfile.activityNames);
    const counts = new Map();
    dataset.requests
      .filter((request) => clusterMembers.includes(request.id_elev) && request.an_scolar === currentYear)
      .forEach((request) => {
        if (!request.nume_activitate || mine.has(request.nume_activitate)) return;
        counts.set(request.nume_activitate, (counts.get(request.nume_activitate) || 0) + 1);
      });
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [dataset, myProfile, clusterMembers, currentYear]);

  const radarData = useMemo(
    () =>
      myProfile && dataset
        ? dataset.modules.map((module) => ({
            modul: module.cod_module,
            credite: myProfile.creditsByModule[module.cod_module] || 0,
            media: classPeers.length
              ? classPeers.reduce((sum, entry) => sum + (entry.creditsByModule[module.cod_module] || 0), 0) / classPeers.length
              : 0,
            tinta: module.credite_tinta || 0,
          }))
        : [],
    [myProfile, classPeers, dataset],
  );
  const radarMax = radarData.reduce(
    (maxValue, entry) => Math.max(maxValue, entry.credite, entry.media, entry.tinta),
    0,
  ) || 1;

  const forecast = myProfile
    ? forecastEndOfYear(myProfile.monthlyCumulative, dataset.targetSum, Math.min(9, Math.max(0, schoolMonthIndex(new Date().toISOString()))))
    : null;

  const historyData = useMemo(() => {
    if (!myProfile) return [];
    const labels = ['sep', 'oct', 'nov', 'dec', 'ian', 'feb', 'mar', 'apr', 'mai', 'iun'];
    const previous = retroProfiles.get(myProfile.studentId);
    return labels.map((luna, index) => ({
      luna,
      curent: myProfile.monthlyCumulative[index] || 0,
      anterior: previous ? previous.monthlyCumulative[index] || 0 : 0,
    }));
  }, [myProfile, retroProfiles]);

  useEffect(() => {
    if (mode !== 0 || !dataset || !profile || suggestions.length === 0) return;
    const signature = suggestions
      .map((suggestion) => `${suggestion.activityId}-${suggestion.module}`)
      .join('|');
    if (loggedSession.current?.signature === signature) return;
    const sessionId = createSessionId();
    loggedSession.current = { signature, sessionId, items: suggestions };
    logAuditEvent({
      actionType: 'CREATE',
      actionDescription: `Sesiune sugestii: ${suggestions.length} recomandari afisate`,
      entityType: 'suggestion_shown',
      entityId: sessionId,
      entityName: `Sesiune sugestii (${suggestions.length})`,
      newValues: {
        id_elev: profile.id,
        an_scolar: currentYear,
        session_id: sessionId,
        suggestions: suggestions.map((suggestion, index) => ({
          activity_id: suggestion.activityId,
          nume: suggestion.activityName,
          module: suggestion.module,
          score: suggestion.finalScore,
          position: index + 1,
        })),
        shown_at: new Date().toISOString(),
      },
    });
  }, [suggestions, profile, mode, dataset, currentYear]);

  const handleOpenDetail = (suggestion) => {
    setDetail(suggestion);
    const position = suggestions.findIndex(
      (item) => item.activityId === suggestion.activityId && item.module === suggestion.module,
    ) + 1;
    logAuditEvent({
      actionType: 'CREATE',
      actionDescription: `Sugestie accesata: ${suggestion.activityName}`,
      entityType: 'suggestion_clicked',
      entityId: suggestion.activityId,
      entityName: suggestion.activityName,
      newValues: {
        id_elev: profile?.id || null,
        session_id: loggedSession.current?.sessionId || null,
        activity_id: suggestion.activityId,
        module: suggestion.module,
        position: position > 0 ? position : null,
        nume_activitate: suggestion.activityName,
        clicked_at: new Date().toISOString(),
      },
    });
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reincarca
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          justifyContent="center"
          alignItems="center"
          minHeight="40vh"
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">Se calculează sugestiile...</Typography>
        </Box>
      </Container>
    );
  }

  if (!dataset) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Alert severity="error">Datele nu au putut fi incarcate. Reincarca pagina.</Alert>
      </Container>
    );
  }

  if (!myProfile) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Alert severity="info">
          Profilul tau nu apare in lista de elevi activi, deci sugestiile nu pot fi calculate.
          Verifica anul scolar selectat sau contacteaza coordonatorul.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Sugestii pentru tine</Typography>
          <Typography variant="body1" color="text.secondary">
            Recomandari calculate din profilul tau, al colegilor si calendarul modulelor.
          </Typography>
        </Box>
        {mode === 1 && (
          <SchoolYearSelect value={retroYear} options={yearOptions.filter((year) => year !== currentYear)} onChange={setRetroYear} />
        )}
      </Box>

      <Tabs value={mode} onChange={(event, value) => setMode(value)} sx={{ mb: 3 }}>
        <Tab label="Prospectiv (an curent)" />
        <Tab label="Retrospectiv (an incheiat)" />
      </Tabs>

      {mode === 0 && (
        <>
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SparkIcon color="primary" />
                Ce sa faci in continuare
              </Typography>
              {suggestions.length === 0 && (
                <Alert severity="info">Nu exista sugestii momentan. Adauga activitati in cererile tale.</Alert>
              )}
              {suggestions.map((suggestion) => (
                <Box key={`${suggestion.activityId}-${suggestion.module}`} sx={{ py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {suggestion.activityName} ({suggestion.module})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        <Chip size="small" variant="outlined" label={CATEGORY_LABELS[suggestion.category] || suggestion.category} />
                        <Chip size="small" label={`${suggestion.estimatedCredits} credite`} />
                        <Chip size="small" label={`scor ${suggestion.finalScore}`} />
                      </Box>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => handleOpenDetail(suggestion)}>
                      Vezi detalii
                    </Button>
                  </Box>
                  {suggestion.explanation.map((line) => (
                    <Typography key={line} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {line}
                    </Typography>
                  ))}
                </Box>
              ))}
            </CardContent>
          </Card>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrophyIcon color="primary" />
                  Unde esti
                </Typography>
                <Box sx={{ width: '100%', height: 260, minWidth: 0 }}>
                  {radarData.length > 0 && (
                  <ResponsiveContainer initialDimension={{ width: 520, height: 260 }}>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="modul" />
                      <PolarRadiusAxis domain={[0, Math.ceil(radarMax * 1.2)]} />
                      <Radar dataKey="credite" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.5} name="Credite" />
                      <Radar dataKey="media" stroke="#10B981" fill="none" strokeDasharray="5 5" name="Media clasei" />
                      <Radar dataKey="tinta" stroke="#000000" fill="#000000" fillOpacity={0.1} name="Tinta" />
                      <Legend />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                  )}
                </Box>
                <Typography variant="body2">
                  Total: <strong>{myProfile.totalCredits}</strong> credite.
                  {forecast && (
                    <> La ritmul actual, vei atinge aproximativ <strong>{Math.round(forecast.estimated)}</strong> credite pana in iunie
                    (probabilitate tinta {(forecast.probability * 100).toFixed(0)}%).</>
                  )}
                </Typography>
                {dataset.modules.map((module) => {
                  const credits = myProfile.creditsByModule[module.cod_module] || 0;
                  const target = module.credite_tinta || 0;
                  return (
                    <Box key={module.cod_module} sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">{module.cod_module} {module.nume_module}</Typography>
                        <Typography variant="caption">{credits}{target > 0 ? ` / ${target}` : ''}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={target > 0 ? Math.min(100, (credits / target) * 100) : credits > 0 ? 100 : 0}
                      />
                    </Box>
                  );
                })}
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GroupsIcon color="primary" />
                  Clusterul tau
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Esti in grupul <strong>{myCluster + 1}</strong> cu <strong>{clusterMembers.length}</strong> elevi similari.
                </Typography>
                {clusterActivities.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Nu exista activitati noi in clusterul tau.</Typography>
                )}
                {clusterActivities.map((entry) => (
                  <Box key={entry.name} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2">{entry.name}</Typography>
                    <Typography variant="body2" fontWeight="bold">{entry.count} colegi</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HistoryIcon color="primary" />
                Istoric credite cumulate
              </Typography>
              <Box sx={{ width: '100%', height: 260, minWidth: 0 }}>
                {historyData.length > 0 && (
                <ResponsiveContainer initialDimension={{ width: 520, height: 260 }}>
                  <LineChart data={historyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="luna" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="curent" stroke="#4F46E5" name={`An ${currentYear}`} />
                    <Line type="monotone" dataKey="anterior" stroke="#000000" strokeDasharray="5 5" name={`An ${retroYear || ''}`} />
                  </LineChart>
                </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {mode === 1 && (
        <>
          {!retrospective && (
            <Alert severity="info">Nu exista date pentru anul selectat.</Alert>
          )}
          {retrospective && (
            <>
              <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Ce ai fi putut face in {retrospective.schoolYear}
                  </Typography>
                  {retrospective.lines.map((line) => (
                    <Typography key={line} variant="body1" sx={{ mb: 0.5 }}>{line}</Typography>
                  ))}
                </CardContent>
              </Card>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Module subreprezentate
                    </Typography>
                    {retrospective.underrepresented.length === 0 && (
                      <Typography variant="body2" color="text.secondary">Toate modulele au fost peste media clasei.</Typography>
                    )}
                    {retrospective.underrepresented.map((cod) => {
                      const average = retrospective.moduleAverages[cod] || 0;
                      const mine = retroProfiles.get(profile.id)?.creditsByModule[cod] || 0;
                      return (
                        <Typography key={cod} variant="body2">
                          {cod}: {mine} credite (media clasei {average.toFixed(1)})
                        </Typography>
                      );
                    })}
                  </CardContent>
                </Card>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Activitati ratate
                    </Typography>
                    {retrospective.missedActivities.length === 0 && (
                      <Typography variant="body2" color="text.secondary">Ai participat la activitatile populare.</Typography>
                    )}
                    {retrospective.missedActivities.map((entry) => (
                      <Typography key={entry.name} variant="body2">
                        {entry.name} ({entry.classmates} colegi)
                      </Typography>
                    ))}
                  </CardContent>
                </Card>
              </Box>
            </>
          )}
        </>
      )}

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detail?.activityName} ({detail?.module})</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Categorie:</strong> {CATEGORY_LABELS[detail?.category] || detail?.category}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Credite estimate:</strong> {detail?.estimatedCredits}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Scor final:</strong> {detail?.finalScore}
          </Typography>
          {detail?.explanation?.map((line) => (
            <Typography key={line} variant="body2" sx={{ mb: 0.5 }}>- {line}</Typography>
          ))}
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2">
            {detail?.descriere || 'Fara descriere suplimentara.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/new-request')}>Trimite cerere</Button>
          <Button onClick={() => setDetail(null)}>Inchide</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
